import { Check } from 'lucide-react';
import type { ParamPreset } from '@/api/types';
import { Pill } from '@/components/widgets/Pill';
import { cn } from '@/lib/utils';

interface Props {
  presets: ParamPreset[];
  value?: string;
  onChange: (presetCode: string) => void;
  disabled?: boolean;
}

const RISK_LEVEL: Record<string, { label: string; tone: 'up' | 'brand' | 'warn' | 'down' | 'purple' }> = {
  XAU_DRYRUN: { label: '极保守 · 干跑', tone: 'up' },
  XAU_HIGH_FREQ: { label: '高频 · 小手数', tone: 'purple' },
  XAU_CONSERVATIVE: { label: '保守', tone: 'brand' },
  XAU_BALANCED: { label: '平衡', tone: 'warn' },
  XAU_AGGRESSIVE: { label: '激进', tone: 'down' },
};

/**
 * 预设组合单选 — §6.3.6 5 选 1 强约束。卡片式选择,清晰展示比例 / 最小手 / 步长。
 */
export function PresetSelector({ presets, value, onChange, disabled }: Props) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-2">
      {presets.map((p) => {
        const selected = p.code === value;
        const meta = RISK_LEVEL[p.code] ?? { label: '自定义', tone: 'brand' as const };
        return (
          <button
            key={p.code}
            type="button"
            onClick={() => !disabled && onChange(p.code)}
            disabled={disabled}
            className={cn(
              'relative text-left rounded-lg border p-3 transition-all',
              selected ? 'border-brand bg-brand/5 ring-2 ring-brand/30' : 'border-line bg-bg-2 hover:bg-bg-3',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {selected && (
              <div className="absolute right-2 top-2 h-5 w-5 rounded-full bg-brand text-white flex items-center justify-center">
                <Check className="h-3 w-3" />
              </div>
            )}
            <Pill tone={meta.tone} className="text-[10px]">
              {meta.label}
            </Pill>
            <div className="mt-2 text-sm font-semibold text-ink-0 truncate">{p.code}</div>
            <div className="mt-2 space-y-1 text-xs text-ink-2">
              <div className="flex justify-between">
                <span>跟单比例</span>
                <span className="num font-medium text-ink-1">{p.followRatio.toFixed(2)}x</span>
              </div>
              <div className="flex justify-between">
                <span>最小手</span>
                <span className="num text-ink-1">{p.virtualMinLot.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>步长</span>
                <span className="num text-ink-1">{p.virtualLotStep.toFixed(2)}</span>
              </div>
            </div>
            {p.description && <div className="mt-2 text-[11px] text-ink-3 line-clamp-2">{p.description}</div>}
            {p.isDefault && <Pill tone="neutral" className="mt-2 text-[10px]">默认</Pill>}
          </button>
        );
      })}
    </div>
  );
}
